/**
 * 보안 메모 컨텍스트
 * 메모 상태 관리 및 비즈니스 로직
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { usePin } from './PinContext';
import {
  createSecureNote,
  getSecureNote,
  getSecureNoteMetadata,
  getSecureNotes,
  updateSecureNote,
  deleteSecureNote,
  searchSecureNotes,
  toggleNoteFavorite,
  archiveNote
} from '../services/secureNotesService';
// PIN 관리는 PinContext를 통해 처리 (Master PIN만 사용)

const SecureNotesContext = createContext();

export function useSecureNotes() {
  const context = useContext(SecureNotesContext);
  if (!context) {
    throw new Error('useSecureNotes must be used within SecureNotesProvider');
  }
  return context;
}

export function SecureNotesProvider({ children }) {
  const { user } = useAuth();
  const { verifyPin, pinSettings, checkUnlockStatus } = usePin();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    searchTerm: '',
    showArchived: false,
    sortBy: 'updatedAt',
    sortOrder: 'desc'
  });

  // 메모 목록 로드 (returns fetched notes for immediate use)
  const loadNotes = useCallback(async () => {
    if (!user) return [];

    setLoading(true);
    setError(null);

    try {
      const fetchedNotes = await getSecureNotes(user.uid, filters);
      setNotes(fetchedNotes);
      return fetchedNotes; // Return for immediate use
    } catch (err) {
      console.error('Error loading notes:', err);
      setError('메모를 불러오는데 실패했습니다.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [user, filters]);

  // 초기 로드
  useEffect(() => {
    if (user) {
      loadNotes();
    }
  }, [user, loadNotes]);

  // 메모 생성
  const createNote = async (noteData, pin = null) => {
    if (!user) throw new Error('User not authenticated');
    
    setLoading(true);
    setError(null);
    
    try {
      const newNote = await createSecureNote(user.uid, noteData);
      
      // Master PIN 사용 시 isPinProtected 플래그만 설정
      if (pin) {
        newNote.isPinProtected = true;
      }
      
      setNotes(prev => [newNote, ...prev]);
      return newNote;
    } catch (err) {
      console.error('Error creating note:', err);
      setError('메모 생성에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 메모 조회 (보안 강화: PIN 검증 후에만 내용 복호화)
  const getNote = async (noteId, pin = null) => {
    if (!user) throw new Error('User not authenticated');

    setLoading(true);
    setError(null);

    try {
      // 1단계: 메타데이터만 먼저 조회 (내용 복호화 안 함)
      const noteMetadata = await getSecureNoteMetadata(noteId, user.uid);

      // 2단계: PIN 보호된 메모인 경우 PIN 검증 먼저 수행
      if (noteMetadata.isPinProtected) {
        // Check if already unlocked globally
        const isUnlocked = checkUnlockStatus();

        if (!isUnlocked) {
          // Not unlocked, require PIN
          if (!pin) {
            throw new Error('PIN이 필요합니다.');
          }

          // 마스터 PIN 먼저 확인, 그 다음 개별 PIN 확인
          const isValid = await verifyPin(pin);
          if (!isValid) {
            throw new Error('잘못된 PIN입니다.');
          }
        }
        // If already unlocked, proceed without PIN verification
      }

      // 3단계: PIN 검증 성공 후에만 전체 내용 복호화
      const note = await getSecureNote(noteId, user.uid);
      return note;
    } catch (err) {
      console.error('Error getting note:', err);
      setError('메모를 불러오는데 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 메모 업데이트 (보안 강화: PIN 검증 후에만 업데이트)
  const updateNote = async (noteId, updates, pin = null) => {
    if (!user) throw new Error('User not authenticated');
    
    setLoading(true);
    setError(null);
    
    try {
      // 1단계: 메타데이터만 먼저 조회하여 PIN 보호 확인
      const noteMetadata = await getSecureNoteMetadata(noteId, user.uid);
      
      // 2단계: PIN 보호된 메모인 경우 PIN 검증 먼저 수행
      if (noteMetadata.isPinProtected) {
        // Check if already unlocked globally
        const isUnlocked = checkUnlockStatus();

        if (!isUnlocked) {
          // Not unlocked, require PIN
          if (!pin) {
            throw new Error('PIN이 필요합니다.');
          }

          const isValid = await verifyPin(pin);
          if (!isValid) {
            throw new Error('잘못된 PIN입니다.');
          }
        }
        // If already unlocked, proceed without PIN verification
      }
      
      // 3단계: PIN 검증 성공 후에만 업데이트 수행
      const updatedNote = await updateSecureNote(noteId, user.uid, updates);
      
      setNotes(prev => prev.map(n => 
        n.id === noteId ? { ...n, ...updatedNote } : n
      ));
      
      return updatedNote;
    } catch (err) {
      console.error('Error updating note:', err);
      setError('메모 업데이트에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 메모 삭제 (보안 강화: PIN 검증 후에만 삭제)
  const deleteNote = async (noteId, pin = null) => {
    if (!user) throw new Error('User not authenticated');

    setLoading(true);
    setError(null);

    try {
      // 1단계: 메타데이터만 먼저 조회하여 PIN 보호 확인
      const noteMetadata = await getSecureNoteMetadata(noteId, user.uid);

      // 2단계: PIN 보호된 메모인 경우 PIN 검증 먼저 수행
      if (noteMetadata.isPinProtected) {
        // PIN이 제공되면 검증, 아니면 현재 언락 상태 확인
        if (pin) {
          const isValid = await verifyPin(pin);
          if (!isValid) {
            throw new Error('잘못된 PIN입니다.');
          }
        } else if (!checkUnlockStatus()) {
          // PIN이 없고 언락되지 않은 상태면 에러
          throw new Error('PIN이 필요합니다.');
        }
        // 언락된 상태면 PIN 없이도 삭제 가능
      }
      
      // 3단계: PIN 검증 성공 후에만 삭제 수행
      await deleteSecureNote(noteId, user.uid);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      
      return true;
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('메모 삭제에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 메모 검색
  const searchNotes = async (searchTerm) => {
    if (!user) return [];
    
    setLoading(true);
    setError(null);
    
    try {
      const results = await searchSecureNotes(user.uid, searchTerm);
      return results;
    } catch (err) {
      console.error('Error searching notes:', err);
      setError('메모 검색에 실패했습니다.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // 즐겨찾기 토글
  const toggleFavorite = async (noteId) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      const isFavorite = await toggleNoteFavorite(noteId, user.uid);
      setNotes(prev => prev.map(n => 
        n.id === noteId ? { ...n, isFavorite } : n
      ));
      return isFavorite;
    } catch (err) {
      console.error('Error toggling favorite:', err);
      setError('즐겨찾기 설정에 실패했습니다.');
      throw err;
    }
  };

  // 아카이브 토글
  const toggleArchive = async (noteId) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      const note = notes.find(n => n.id === noteId);
      const isArchived = !note?.isArchived;
      
      await archiveNote(noteId, user.uid, isArchived);
      setNotes(prev => prev.map(n => 
        n.id === noteId ? { ...n, isArchived } : n
      ));
      
      return isArchived;
    } catch (err) {
      console.error('Error toggling archive:', err);
      setError('아카이브 설정에 실패했습니다.');
      throw err;
    }
  };

  // PIN 보호 설정 (Master PIN만 사용)
  const setPinForNote = async (noteId) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      // isPinProtected 플래그만 업데이트
      await updateSecureNote(noteId, user.uid, { isPinProtected: true });
      setNotes(prev => prev.map(n => 
        n.id === noteId ? { ...n, isPinProtected: true } : n
      ));
      return true;
    } catch (err) {
      console.error('Error setting PIN protection:', err);
      setError('PIN 보호 설정에 실패했습니다.');
      throw err;
    }
  };

  // Master PIN만 사용하므로 개별 PIN 변경 기능 제거
  const changePinForNote = async () => {
    throw new Error('Individual PIN change is not supported. Use Master PIN instead.');
  };

  // PIN 보호 제거 (Master PIN만 사용)
  const removePinForNote = async (noteId) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      // isPinProtected 플래그만 업데이트
      await updateSecureNote(noteId, user.uid, { isPinProtected: false });
      setNotes(prev => prev.map(n => 
        n.id === noteId ? { ...n, isPinProtected: false } : n
      ));
      return true;
    } catch (err) {
      console.error('Error removing PIN protection:', err);
      setError('PIN 보호 제거에 실패했습니다.');
      throw err;
    }
  };

  // 필터 업데이트
  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // 필터 초기화
  const resetFilters = () => {
    setFilters({
      category: '',
      searchTerm: '',
      showArchived: false,
      sortBy: 'updatedAt',
      sortOrder: 'desc'
    });
  };

  const value = {
    notes,
    loading,
    error,
    filters,
    createNote,
    getNote,
    updateNote,
    deleteNote,
    searchNotes,
    toggleFavorite,
    toggleArchive,
    setPinForNote,
    changePinForNote,
    removePinForNote,
    updateFilters,
    resetFilters,
    loadNotes
  };

  return (
    <SecureNotesContext.Provider value={value}>
      {children}
    </SecureNotesContext.Provider>
  );
}